import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { doc, getDoc } from "firebase/firestore";
import emailjs from "@emailjs/browser";
import adyantamLogo from "../assets/adyantam-logo.png";

const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function LoginScreen() {
  const { login, generateResetToken } = useAuth();

  // Joins the selected country code with the typed number.
  // Indian numbers stay as plain 10 digits (matches existing Firestore data).
  // US numbers get the +1 prefix attached.
  function buildFullNumber(code, number) {
    return code === "+91" ? number : `${code}${number}`;
  }


  const [mobile, setMobile]       = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [password, setPassword]   = useState("");
  const [showPass, setShowPass]   = useState(false);
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);

  // ── Forgot Password state ──
  const [mode, setMode]               = useState("login"); // "login" | "forgot" | "watchman"
  const [forgotMobile, setForgotMobile] = useState("");
  const [forgotCountryCode, setForgotCountryCode] = useState("+91");
  const [forgotError, setForgotError]   = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent]       = useState(false);

  // ── Watchman PIN login state ──
  const [wMobile, setWMobile] = useState("");
  const [wPin, setWPin]       = useState("");
  const [wError, setWError]   = useState("");
  const [wLoading, setWLoading] = useState(false);

  // ── Get Started (onboarding interest) state ──
  const [gsName, setGsName]         = useState("");
  const [gsMobile, setGsMobile]     = useState("");
  const [gsCountryCode, setGsCountryCode] = useState("+91");
  const [gsEmail, setGsEmail]       = useState("");
  const [gsSociety, setGsSociety]   = useState("");
  const [gsCity, setGsCity]         = useState("");
  const [gsReferred, setGsReferred] = useState(null); // null | "yes" | "no"
  const [gsSalesName, setGsSalesName]     = useState("");
  const [gsSalesMobile, setGsSalesMobile] = useState("");
  const [gsError, setGsError]       = useState("");
  const [gsLoading, setGsLoading]   = useState(false);
  const [gsSubmitted, setGsSubmitted] = useState(false);

  // Society info from Firestore settings/init
  const [societyName, setSocietyName] = useState("");
  const [location, setLocation]       = useState("");
  const [totalFlats, setTotalFlats]   = useState("");
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    async function fetchSettings() {
      try {
        const snap = await getDoc(doc(db, "settings", "init"));
        if (snap.exists()) {
          const d = snap.data();
          setSocietyName(d.societyName || "");
          setLocation(d.location || "");
          setTotalFlats(d.totalFlats || "");
        }
      } catch (e) {
        // silently fail — fallback text shown
      } finally {
        setSettingsLoading(false);
      }
    }
    fetchSettings();
  }, []);

  async function handleLogin(e) {
    e.preventDefault();
    setError("");

    if (mobile.length !== 10 || !/^\d{10}$/.test(mobile)) {
      setError("Enter a valid 10-digit mobile number.");
      return;
    }
    if (!password) {
      setError("Password is required.");
      return;
    }

    setLoading(true);
    const fullNumber = buildFullNumber(countryCode, mobile);
    const result = await login(fullNumber, password);
    setLoading(false);

    if (!result.success) {
      setError(result.error);
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    setForgotError("");

    if (forgotMobile.length !== 10 || !/^\d{10}$/.test(forgotMobile)) {
      setForgotError("Enter a valid 10-digit mobile number.");
      return;
    }

    setForgotLoading(true);
    const fullNumber = buildFullNumber(forgotCountryCode, forgotMobile);
    const result = await generateResetToken(fullNumber);
    if (!result.success) {
      setForgotLoading(false);
      setForgotError(result.error);
      return;
    }

    try {
      const resetLink = `${window.location.origin}/reset-password?token=${result.token}`;
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          to_email: result.email,
          to_name: result.name,
          link: resetLink,
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      setForgotSent(true);
    } catch (err) {
      setForgotError("Could not send email. Please try again or contact your MC member.");
    } finally {
      setForgotLoading(false);
    }
  }

  function resetForgotState() {
    setMode("login");
    setForgotMobile("");
    setForgotCountryCode("+91");
    setForgotError("");
    setForgotSent(false);
    setForgotLoading(false);
  }

  // ── Watchman PIN login ──
  function resetWatchmanState() {
    setMode("login");
    setWMobile("");
    setWPin("");
    setWError("");
    setWLoading(false);
  }

  // ── Get Started (onboarding interest) ──
  function resetGetStartedState() {
    setMode("login");
    setGsName("");
    setGsMobile("");
    setGsCountryCode("+91");
    setGsEmail("");
    setGsSociety("");
    setGsCity("");
    setGsReferred(null);
    setGsSalesName("");
    setGsSalesMobile("");
    setGsError("");
    setGsLoading(false);
    setGsSubmitted(false);
  }

  async function handleGetStarted(e) {
    e.preventDefault();
    setGsError("");

    if (!gsName.trim()) {
      setGsError("Please enter your name.");
      return;
    }
    if (gsMobile.length !== 10 || !/^\d{10}$/.test(gsMobile)) {
      setGsError("Enter a valid 10-digit mobile number.");
      return;
    }
    if (!gsEmail.trim() || !/^\S+@\S+\.\S+$/.test(gsEmail)) {
      setGsError("Enter a valid email address.");
      return;
    }
    if (!gsSociety.trim()) {
      setGsError("Please enter your society/building name.");
      return;
    }
    if (!gsCity.trim()) {
      setGsError("Please enter your city.");
      return;
    }
    if (gsReferred === null) {
      setGsError("Please let us know if a sales person referred you.");
      return;
    }
    if (gsReferred === "yes") {
      if (!gsSalesName.trim()) {
        setGsError("Please enter the sales person's name.");
        return;
      }
      if (gsSalesMobile.length !== 10 || !/^\d{10}$/.test(gsSalesMobile)) {
        setGsError("Enter a valid 10-digit mobile number for the sales person.");
        return;
      }
    }

    setGsLoading(true);
    try {
      const response = await fetch(
        "https://asia-south1-abc-dashboard001.cloudfunctions.net/submitLead",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            prospectName: gsName,
            prospectMobile: buildFullNumber(gsCountryCode, gsMobile),
            prospectEmail: gsEmail,
            societyName: gsSociety,
            city: gsCity,
            referredBySales: gsReferred === "yes",
            salesPersonName: gsSalesName,
            salesPersonMobile: gsSalesMobile,
          }),
        }
      );
      const result = await response.json();
      setGsLoading(false);
      if (!response.ok || !result.success) {
        setGsError(result.error || "Could not submit. Please try again.");
        return;
      }
      setGsSubmitted(true);
    } catch (err) {
      setGsLoading(false);
      setGsError("Could not submit. Please check your connection and try again.");
    }
  }

  function wPressDigit(d) {
    setWError("");
    setWPin(prev => (prev.length < 4 ? prev + d : prev));
  }
  function wPressBackspace() {
    setWError("");
    setWPin(prev => prev.slice(0, -1));
  }

  async function handleWatchmanLogin(pin) {
    if (wMobile.length !== 10 || !/^\d{10}$/.test(wMobile)) {
      setWError("Enter the 10-digit mobile number.");
      return;
    }
    if (pin.length !== 4) {
      setWError("Enter the 4-digit PIN.");
      return;
    }
    setWLoading(true);
    const result = await login(wMobile, pin);
    setWLoading(false);
    if (!result.success) {
      setWError(result.error);
      setWPin("");
    }
  }

  // Auto-submit once 4 digits entered
  useEffect(() => {
    if (mode === "watchman" && wPin.length === 4 && !wLoading) {
      handleWatchmanLogin(wPin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wPin]);


  const subtitleParts = [];
  if (societyName) subtitleParts.push(societyName);
  if (location)    subtitleParts.push(location);
  if (totalFlats)  subtitleParts.push(`${totalFlats} Flats`);
  const subtitle = settingsLoading
    ? "Loading…"
    : subtitleParts.length > 0
      ? subtitleParts.join(" · ")
      : "Adyantam Building Circle®";

  return (
    <div style={styles.page}>

      {/* ── Adyantam Brand Header ── */}
      <div style={styles.brandHeader}>
        <img src={adyantamLogo} alt="Adyantam" style={styles.brandLogo} />
        <div style={styles.brandName}>Adyantam Building Circle®</div>
        <div style={styles.brandTagline}>Empowering Resident Communities</div>
      </div>

      {/* ── Society info strip ── */}
      <div style={styles.societyStrip}>
        <span style={styles.societyIcon}>🏢</span>
        <span style={styles.societyText}>{subtitle}</span>
      </div>

      {/* ── Login Card ── */}
      {mode === "login" && (
      <form onSubmit={handleLogin} style={styles.card} noValidate>
        <div style={styles.cardTitle}>Sign In</div>

        {/* Mobile */}
        <div style={styles.field}>
          <label style={styles.label}>Mobile Number</label>
          <div style={styles.phoneRow}>
            <select
              value={countryCode}
              onChange={(e) => { setError(""); setCountryCode(e.target.value); }}
              style={styles.countrySelect}
            >
              <option value="+91">🇮🇳 +91</option>
              <option value="+1">🇺🇸 +1</option>
            </select>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="10-digit number"
              value={mobile}
              onChange={(e) => {
                setError("");
                setMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
              }}
              style={{ ...styles.input, ...styles.phoneInput }}
              autoComplete="username"
            />
          </div>
          <div style={styles.countryHint}>
            🇮🇳 Indian number: select +91 and type 10 digits &nbsp;|&nbsp; 🇺🇸 US number: select +1 and type 10 digits
          </div>
        </div>

        {/* Password */}
        <div style={styles.field}>
          <label style={styles.label}>Password</label>
          <div style={styles.passWrap}>
            <input
              type={showPass ? "text" : "password"}
              placeholder="Enter password"
              value={password}
              onChange={(e) => { setError(""); setPassword(e.target.value); }}
              style={{ ...styles.input, paddingRight: "44px" }}
              autoComplete="current-password"
            />
            <button
              type="button"
              style={styles.eyeBtn}
              onClick={() => setShowPass((v) => !v)}
              tabIndex={-1}
            >
              {showPass ? "🙈" : "👁️"}
            </button>
          </div>
        </div>

        {/* Error */}
        <div style={styles.errorBox}>
          {error && <span style={styles.errorText}>⚠ {error}</span>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          style={{ ...styles.btn, opacity: loading ? 0.7 : 1 }}
          disabled={loading}
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>

        <div style={styles.hint}>
          <span style={styles.linkText} onClick={() => setMode("forgot")}>
            Forgot password?
          </span>
        </div>
        <div style={styles.hint}>
          <span style={styles.linkText} onClick={() => setMode("watchman")}>
            🧮 Watchman Login
          </span>
        </div>

        <div style={styles.demoBox}>
          <div style={styles.demoTitle}>Demo logins (password for all: Demo@123)</div>
          <div style={styles.demoLine}>President  → 9999900001</div>
          <div style={styles.demoLine}>Secretary  → 9999900002</div>
          <div style={styles.demoLine}>Treasurer  → 9999900003</div>
          <div style={styles.demoLine}>FlatOwner  → 9999900004</div>
          <div style={styles.demoLine}>Tenant     → 9999900005</div>
          <div style={styles.demoLine}>Watchman   → 9999900006</div>
        </div>

        {/* ── Get Started (onboarding interest) button ── */}
        <button
          type="button"
          style={styles.getStartedBtn}
          onClick={() => setMode("getstarted")}
        >
          Interested? Get Started →
        </button>
      </form>
      )}

      {/* ── Forgot Password Card ── */}
      {mode === "forgot" && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Reset Password</div>

          {!forgotSent ? (
            <form onSubmit={handleForgotPassword} noValidate>
              <div style={styles.field}>
                <label style={styles.label}>Registered Mobile Number</label>
                <div style={styles.phoneRow}>
                  <select
                    value={forgotCountryCode}
                    onChange={(e) => { setForgotError(""); setForgotCountryCode(e.target.value); }}
                    style={styles.countrySelect}
                  >
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+1">🇺🇸 +1</option>
                  </select>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                    value={forgotMobile}
                    onChange={(e) => {
                      setForgotError("");
                      setForgotMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                    }}
                    style={{ ...styles.input, ...styles.phoneInput }}
                    autoComplete="off"
                  />
                </div>
                <div style={styles.countryHint}>
                  🇮🇳 Indian number: select +91 &nbsp;|&nbsp; 🇺🇸 US number: select +1
                </div>
              </div>

              <div style={styles.errorBox}>
                {forgotError && <span style={styles.errorText}>⚠ {forgotError}</span>}
              </div>

              <button
                type="submit"
                style={{ ...styles.btn, opacity: forgotLoading ? 0.7 : 1 }}
                disabled={forgotLoading}
              >
                {forgotLoading ? "Sending…" : "Send Reset Link"}
              </button>

              <div style={styles.hint}>
                <span style={styles.linkText} onClick={resetForgotState}>
                  ← Back to Sign In
                </span>
              </div>
            </form>
          ) : (
            <div>
              <div style={styles.successText}>
                ✅ Reset link sent! Check your registered email inbox
                (and spam folder) for a message from {societyName || "your society"}.
                The link is valid for 1 hour.
              </div>
              <button
                type="button"
                style={{ ...styles.btn, marginTop: "16px" }}
                onClick={resetForgotState}
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Watchman PIN Login Card ── */}
      {mode === "watchman" && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>🧮 Watchman Login</div>

          <div style={styles.field}>
            <label style={styles.label}>Mobile Number</label>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="10-digit mobile"
              value={wMobile}
              onChange={(e) => {
                setWError("");
                setWMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
              }}
              style={styles.input}
              autoComplete="username"
            />
          </div>

          {/* PIN display */}
          <div style={{ textAlign: "center", margin: "8px 0 16px" }}>
            <div style={styles.label}>Enter 4-Digit PIN</div>
            <div style={{
              display: "flex", justifyContent: "center", gap: "10px", marginTop: "8px",
            }}>
              {[0, 1, 2, 3].map(i => (
                <div key={i} style={{
                  width: "44px", height: "52px", borderRadius: "10px",
                  background: "#0f0f13", border: "1px solid #2e2e40",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "24px", fontWeight: "700", color: "#e8e8f0",
                }}>
                  {wPin[i] ? "●" : ""}
                </div>
              ))}
            </div>
          </div>

          <div style={styles.errorBox}>
            {wError && <span style={styles.errorText}>⚠ {wError}</span>}
            {wLoading && <span style={{ fontSize: "13px", color: "#8888a8" }}>Signing in…</span>}
          </div>

          {/* Numeric keypad */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginBottom: "14px" }}>
            {["1","2","3","4","5","6","7","8","9"].map(d => (
              <button
                key={d}
                type="button"
                onClick={() => wPressDigit(d)}
                disabled={wLoading}
                style={{
                  padding: "16px", fontSize: "20px", fontWeight: "700",
                  background: "#0f0f13", color: "#e8e8f0", border: "1px solid #2e2e40",
                  borderRadius: "10px", cursor: wLoading ? "not-allowed" : "pointer",
                }}
              >
                {d}
              </button>
            ))}
            <div />
            <button
              type="button"
              onClick={() => wPressDigit("0")}
              disabled={wLoading}
              style={{
                padding: "16px", fontSize: "20px", fontWeight: "700",
                background: "#0f0f13", color: "#e8e8f0", border: "1px solid #2e2e40",
                borderRadius: "10px", cursor: wLoading ? "not-allowed" : "pointer",
              }}
            >
              0
            </button>
            <button
              type="button"
              onClick={wPressBackspace}
              disabled={wLoading}
              style={{
                padding: "16px", fontSize: "18px", fontWeight: "700",
                background: "#0f0f13", color: "#ffa726", border: "1px solid #2e2e40",
                borderRadius: "10px", cursor: wLoading ? "not-allowed" : "pointer",
              }}
            >
              ⌫
            </button>
          </div>

          <div style={styles.hint}>
            <span style={styles.linkText} onClick={resetWatchmanState}>
              ← Back to Sign In
            </span>
          </div>
        </div>
      )}

      {/* ── Get Started (onboarding interest) Card ── */}
      {mode === "getstarted" && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Get Started</div>

          {!gsSubmitted ? (
            <form onSubmit={handleGetStarted} noValidate>
              <div style={styles.field}>
                <label style={styles.label}>Your Name</label>
                <input
                  type="text"
                  placeholder="Full name"
                  value={gsName}
                  onChange={(e) => { setGsError(""); setGsName(e.target.value); }}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Mobile Number</label>
                <div style={styles.phoneRow}>
                  <select
                    value={gsCountryCode}
                    onChange={(e) => { setGsError(""); setGsCountryCode(e.target.value); }}
                    style={styles.countrySelect}
                  >
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+1">🇺🇸 +1</option>
                  </select>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                    value={gsMobile}
                    onChange={(e) => {
                      setGsError("");
                      setGsMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                    }}
                    style={{ ...styles.input, ...styles.phoneInput }}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Email Address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={gsEmail}
                  onChange={(e) => { setGsError(""); setGsEmail(e.target.value); }}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Society / Building Name</label>
                <input
                  type="text"
                  placeholder="e.g. Sunrise Residency"
                  value={gsSociety}
                  onChange={(e) => { setGsError(""); setGsSociety(e.target.value); }}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>City</label>
                <input
                  type="text"
                  placeholder="e.g. Hyderabad"
                  value={gsCity}
                  onChange={(e) => { setGsError(""); setGsCity(e.target.value); }}
                  style={styles.input}
                />
              </div>

              <div style={{ ...styles.field, borderTop: "1px solid #2e2e40", paddingTop: "16px" }}>
                <label style={styles.label}>Were you referred by an Adyantam sales person?</label>
                <div style={{ display: "flex", gap: "10px", marginTop: "6px" }}>
                  <button
                    type="button"
                    onClick={() => { setGsError(""); setGsReferred("yes"); }}
                    style={{
                      ...styles.toggleBtn,
                      ...(gsReferred === "yes" ? styles.toggleBtnActive : {}),
                    }}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGsError("");
                      setGsReferred("no");
                      setGsSalesName("");
                      setGsSalesMobile("");
                    }}
                    style={{
                      ...styles.toggleBtn,
                      ...(gsReferred === "no" ? styles.toggleBtnActive : {}),
                    }}
                  >
                    No
                  </button>
                </div>
              </div>

              {gsReferred === "yes" && (
                <div style={styles.salesBox}>
                  <div style={styles.field}>
                    <label style={styles.label}>Sales Person Name</label>
                    <input
                      type="text"
                      placeholder="Full name"
                      value={gsSalesName}
                      onChange={(e) => { setGsError(""); setGsSalesName(e.target.value); }}
                      style={styles.input}
                    />
                  </div>
                  <div style={{ ...styles.field, marginBottom: 0 }}>
                    <label style={styles.label}>Sales Person Mobile Number</label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10-digit number"
                      value={gsSalesMobile}
                      onChange={(e) => {
                        setGsError("");
                        setGsSalesMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                      }}
                      style={styles.input}
                    />
                  </div>
                </div>
              )}

              <div style={styles.errorBox}>
                {gsError && <span style={styles.errorText}>⚠ {gsError}</span>}
              </div>

              <button
                type="submit"
                style={{ ...styles.btn, opacity: gsLoading ? 0.7 : 1 }}
                disabled={gsLoading}
              >
                {gsLoading ? "Submitting…" : "Submit"}
              </button>

              <div style={styles.hint}>
                <span style={styles.linkText} onClick={resetGetStartedState}>
                  ← Back to Sign In
                </span>
              </div>
            </form>
          ) : (
            <div>
              <div style={styles.successText}>
                ✅ Thank you, {gsName || "there"}! Our team will reach out to you shortly
                to help set up {gsSociety || "your society"}.
                {gsReferred === "yes" && gsSalesName
                  ? ` (Referred by ${gsSalesName}.)`
                  : ""}
              </div>
              <button
                type="button"
                style={{ ...styles.btn, marginTop: "16px" }}
                onClick={resetGetStartedState}
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      )}

      <div style={styles.version}>
        <div style={{ fontWeight: "600", color: "#8888a8", marginBottom: "3px" }}>Adyantam Building Circle® · Empowering Resident Communities</div>
        <div style={{ color: "#6666a0" }}>© {new Date().getFullYear()} Adyantam. All rights reserved. · v1.0.0</div>
        <div style={{ marginTop: "3px", fontStyle: "italic", color: "#555580" }}>you don't need to know code to build — Conceptualised by Dr. Vijay DSK, powered by AI</div>
      </div>

    </div>

  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0f0f13",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  brandHeader: {
    textAlign: "center",
    marginBottom: "12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  brandLogo: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    objectFit: "cover",
    marginBottom: "8px",
    border: "2px solid #6c63ff40",
  },
  brandName: {
    fontSize: "26px",
    fontWeight: "700",
    color: "#6c63ff",
    letterSpacing: "3px",
  },
  brandTagline: {
    fontSize: "11px",
    color: "#555570",
    marginTop: "2px",
    letterSpacing: "0.5px",
  },
  societyStrip: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "#1a1a24",
    border: "1px solid #2e2e40",
    borderRadius: "20px",
    padding: "6px 14px",
    marginBottom: "24px",
  },
  societyIcon: {
    fontSize: "14px",
  },
  societyText: {
    fontSize: "12px",
    color: "#8888a8",
  },
  card: {
    background: "#1a1a24",
    border: "1px solid #2e2e40",
    borderRadius: "16px",
    padding: "28px 24px",
    width: "100%",
    maxWidth: "360px",
  },
  cardTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#e8e8f0",
    marginBottom: "20px",
  },
  field: {
    marginBottom: "16px",
  },
  label: {
    display: "block",
    fontSize: "12px",
    color: "#8888a8",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    background: "#0f0f13",
    border: "1px solid #2e2e40",
    borderRadius: "10px",
    padding: "12px 14px",
    color: "#e8e8f0",
    fontSize: "15px",
    outline: "none",
    boxSizing: "border-box",
  },
  phoneRow: {
    display: "flex",
    gap: "8px",
  },
  countrySelect: {
    background: "#0f0f13",
    border: "1px solid #2e2e40",
    borderRadius: "10px",
    padding: "12px 8px",
    color: "#e8e8f0",
    fontSize: "14px",
    outline: "none",
    flex: "0 0 92px",
  },
  phoneInput: {
    flex: 1,
  },
  countryHint: {
    fontSize: "11px",
    color: "#6666a0",
    marginTop: "6px",
    lineHeight: "1.5",
  },
  passWrap: {
    position: "relative",
  },
  eyeBtn: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    padding: "0",
    lineHeight: 1,
  },
  errorBox: {
    minHeight: "20px",
    marginBottom: "12px",
  },
  errorText: {
    fontSize: "13px",
    color: "#ff5252",
  },
  btn: {
    width: "100%",
    background: "#6c63ff",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "13px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "opacity 0.2s",
  },
  hint: {
    textAlign: "center",
    fontSize: "12px",
    color: "#555570",
    marginTop: "16px",
  },
  linkText: {
    color: "#6c63ff",
    cursor: "pointer",
    textDecoration: "underline",
  },
  demoBox: {
    marginTop: "18px",
    background: "#11111a",
    border: "1px solid #2e2e40",
    borderRadius: "10px",
    padding: "12px 14px",
  },
  demoTitle: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#cfcff8",
    marginBottom: "8px",
    lineHeight: 1.5,
  },
  demoLine: {
    fontSize: "12px",
    color: "#9ea0c8",
    lineHeight: 1.7,
    fontFamily: 'Consolas, "Courier New", monospace',
    whiteSpace: "pre-wrap",
  },
  successText: {
    fontSize: "13px",
    color: "#8888a8",
    lineHeight: "1.6",
    textAlign: "center",
  },
  toggleBtn: {
    flex: 1,
    background: "#0f0f13",
    color: "#8888a8",
    border: "1px solid #2e2e40",
    borderRadius: "10px",
    padding: "11px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  toggleBtnActive: {
    background: "#6c63ff22",
    color: "#6c63ff",
    border: "1px solid #6c63ff",
  },
  salesBox: {
    background: "#11111a",
    border: "1px solid #2e2e40",
    borderRadius: "10px",
    padding: "14px",
    marginBottom: "16px",
  },
  getStartedBtn: {
    width: "100%",
    marginTop: "16px",
    background: "transparent",
    color: "#6c63ff",
    border: "1px solid #6c63ff",
    borderRadius: "10px",
    padding: "13px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  version: {
    marginTop: "24px",
    fontSize: "10px",
    color: "#6666a0",
    textAlign: "center",
    lineHeight: "1.7",
    maxWidth: "360px",
  },
};
